/**
 * PublicBrandBadge — subtle "Made with GeoMarkup" attribution shown on public
 * shared / embedded maps. Links to the marketing site in the active UI language.
 *
 * @module components/share/public/PublicMapShell/PublicBrandBadge
 */
import { memo } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { buildMarketingUrl } from '@/constants';
import { useIsDarkBasemap } from '@/hooks/useBasemapStyle';

import styles from './PublicBrandBadge.module.css';

interface PublicBrandBadgeProps {
  className?: string;
}

export const PublicBrandBadge = memo(function PublicBrandBadge({
  className,
}: PublicBrandBadgeProps) {
  const { t, i18n } = useTranslation();
  // Contrast the attribution chip against the BASEMAP (not the app color
  // scheme): a light chip over a dark basemap, a dark chip over a light one —
  // otherwise the chip blends into the map (dark-on-dark / light-on-light).
  const isDarkBasemap = useIsDarkBasemap();
  const href = buildMarketingUrl(i18n.language);

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={clsx(
        styles.badge,
        isDarkBasemap ? styles.badgeOnDarkMap : styles.badgeOnLightMap,
        className
      )}
    >
      {t('share.public.brand')}
    </a>
  );
});
